<!-- Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved. -->

<template>
  <div class="container">
    <welcome-header />
    <p class="description">
      <span class="title">
        {{ $t("auth.claimAccount.pleaseCreatePassword") }}
        <strong class="email">{{ email }}</strong
        >.
      </span>
      <a @click="notYou">{{ $t("auth.claimAccount.notYou") }}</a>
    </p>
    <form @submit.prevent>
      <input-comp
        class="password"
        :id="formRefs.password"
        :name="formRefs.password"
        :ref="formRefs.password"
        type="password"
        :label="$t('auth.claimAccount.password12')"
        v-model="formData.password"
        v-model:errorMessages="formData.passwordErrors"
        :validationRules="[required, min(minimumPasswordLength)]"
      ></input-comp>
      <input-comp
        class="password-confirm"
        :id="formRefs.passwordConfirm"
        :name="formRefs.passwordConfirm"
        :ref="formRefs.passwordConfirm"
        type="password"
        :label="$t('auth.claimAccount.confirmPassword')"
        v-model="formData.passwordConfirm"
        v-model:errorMessages="formData.passwordConfirmErrors"
        :validationRules="[required, min(minimumPasswordLength), passwordMatches(formData.password)]"
        @input="checkPasswordMatch"
      ></input-comp>
      <button-comp
        @click="submitClaim"
        type="submit"
        large
        legacyOrange
      >
        {{ $t("common.continue") }}
      </button-comp>
      <p v-if="allowGuest" class="guest-checkout">
        <button-comp
          @click="guestCheckout"
          anchorStyle
        >
          {{ $t("auth.claimAccount.checkoutAsGuest") }}
        </button-comp>
      </p>
    </form>
  </div>
</template>

<script lang="ts">
import welcomeHeader from '@/components/auth/welcome-header.vue';
import { postSignInAsync } from '@/api/userApi';
import { isError } from '@/utils/result';
import { isAbortError } from '@/utils/apiUtils';
import { IErrorMessage, formValidator, required, min } from '@/val/customValidation';
import { mutationTypes as infoMutations } from '@/store/info';
import { actionTypes as userActions } from '@/store/user';
import passwordValidation from '@/val/passwordValidation';
import { FieldLengths } from '@/models/constants';

export default {
  setup() {
    return {
      min,
      required,
      formValidator,
      ...passwordValidation,
    };
  },
  name: 'claim-account-view',
  props: ['destination', 'userId', 'token', 'allowGuest'],
  components: { welcomeHeader },
  data() {
    return {
      formData: {
        password: '',
        passwordErrors: [] as IErrorMessage[],
        passwordConfirm: '',
        passwordConfirmErrors: [] as IErrorMessage[],
      },
      formRefs: {
        password: 'password',
        passwordConfirm: 'passwordConfirm',
      },
      name: '',
      email: '',
      minimumPasswordLength: FieldLengths.Password.min,
    };
  },
  created: function() {
    const user = this.$store.state.user.user;
    if (user) {
      this.name = user.firstName;
      this.email = user.email;
    }
  },
  methods: {
    async submitClaim() {
      const validationResults = this.formValidator(this, this.formRefs);

      if (validationResults.length > 0) {
        return;
      }

      try {
        const response = await this.$store.dispatch(userActions.CLAIM_ACCOUNT, {
          destination: this.destination,
          token: this.token,
          userId: this.userId,
          password: this.formData.password,
        });

        if (!response.successResponse) {
          throw Error(response.status);
        }

        const signInResponse = await postSignInAsync({
          email: this.email,
          password: this.formData.password,
          returnUrl: null,
        });

        if (isAbortError(signInResponse)) {
          return;
        }

        if (isError(signInResponse)) {
          // @ts-ignore
          throw Error(signInResponse.status);
        } else {
          (window as Window).location = this.destination;
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        this.$store.commit(infoMutations.ADD_TOAST, {
          color: 'red',
          icon: 'warning_small',
          timer: 6000,
          text: this.$t('user.changePassword.error'),
        });
      }

    },
    guestCheckout() {
      const guestUri = new URL(this.destination);
      guestUri.searchParams.append('cag', '1');
      (window as Window).location = guestUri.toString();
    },
    notYou() {
      (window as Window).location = this.destination;
    },
    checkPasswordMatch() {
      this.formValidator(this, [this.formRefs.passwordConfirm]);
    },
  },
};
</script>

<style lang="scss" scoped>
.password {
  margin-bottom: 30px;

  &-confirm {
    margin-bottom: 40px;
  }
}
.description {
  margin-top: 30px;
  color: $primary-gray;
}

form {
  margin-top: 30px;
  max-width: 540px;
}

.guest-checkout {
  margin-top: 20px;
}
</style>
